import { isHourlyValue } from "@/offering_v2/components/date-time-picker/date_time_picker";
import React, { Fragment } from "react";

import { Container, Row } from "../components";
import {
  getStartDateFromOrderParams,
  getEndDateFromOrderParams,
} from "./order_params_utils";
import { useOfferingDailyAccessQuantityFieldFeatureFlag } from "../core/splitio";
import { PricingType, Space } from "../data";
import {
  PartnerOfferingLocation,
  TemplateName,
} from "../data/partner_offering";
import { formatCurrency } from "../lib/currency/currency";
import { FirstDayOfWeek } from "../lib/datetime";
import { useI18n } from "../lib/i18n/i18n_provider";
import { getSystemLocale } from "../lib/locale/locale";
import {
  AvailableTimeSlot,
  SpaceOffering,
  Quote,
  useSpaceBookingPolicyQuery,
} from "./api";
import { Button } from "./button";
import { CapacityPicker } from "./capacity_picker";
import { useContactForm } from "./contact_form_provider";
import { DateRangePicker } from "./date_range_picker";
import { Divider } from "./divider";
import { useIsWework } from "./hooks/use_is_wework";
import { LocationSpacePicker } from "./location_space_picker";
import { MeetingRoomDateRangePicker } from "./meeting_room_date_range_picker";
import { MultiPassPicker } from "./multipass_picker";
import {
  DailyAccessOrderParams,
  DailyAccessOrderParamsErrors,
  DayOfficeOrderParams,
  DayOfficeOrderParamsErrors,
  DayPassDateRange,
  DayPassOrderParams,
  DayPassOrderParamsErrors,
  DayPassWithCapacityOrderParams,
  DayPassWithCapacityOrderParamsErrors,
  MeetingRoomDateRange,
  MeetingRoomOrderParams,
  MultiDayOfficeOrderParams,
  MultiPassOrderParams,
  OrderParams,
} from "./order_params";
import { PartnerOfferingLocationPicker } from "./partner_location_picker";
import { QuantityPicker } from "./quantity_picker";
import { Spacer } from "./spacer";
import { Text } from "./text";

export const pickerOverride = {
  view: {
    style: {
      paddingLeft: 4,
      paddingRight: 0,
      paddingTop: 4,
      paddingBottom: 4,
      borderRadius: 7,
    },
  },
};

interface MultiCheckoutBoxMobileProps {
  quote?: Quote;
  locations: PartnerOfferingLocation[];
  selectedSpace?: Space | null;
  multiPasses: SpaceOffering[];
  onChangeLocation: (spaceID: string) => void;
  onChangeMultiPass: (addOnID: string, spaceOfferingID: string) => void;
  onContinue: () => void;
}

interface MultiPassCheckoutBoxMobileProps extends MultiCheckoutBoxMobileProps {
  orderParams: MultiPassOrderParams;
}

export function MultiPassCheckoutBoxMobile(
  props: MultiPassCheckoutBoxMobileProps
) {
  const {
    quote,
    orderParams,
    locations,
    multiPasses,
    selectedSpace,
    onChangeLocation,
    onChangeMultiPass,
    onContinue,
  } = props;

  return (
    <CheckoutBoxMobile
      quote={quote}
      orderParams={orderParams}
      selectedSpace={selectedSpace}
      onContinue={onContinue}
      pickers={[
        <LocationSpacePicker
          value={orderParams.spaceID}
          locations={locations}
          onChange={onChangeLocation}
          override={pickerOverride}
        />,
        <MultiPassPicker
          multiPasses={multiPasses}
          value={orderParams.addOnID}
          onChange={onChangeMultiPass}
          override={pickerOverride}
        />,
      ]}
    />
  );
}

interface MultiDayOfficeCheckoutBoxMobileProps
  extends MultiCheckoutBoxMobileProps {
  orderParams: MultiDayOfficeOrderParams;
  capacityOptionLabel?: string;
  capacities: number[];
  onChangeCapacity: (qty: number) => void;
}

export function MultiDayOfficeCheckoutBoxMobile(
  props: MultiDayOfficeCheckoutBoxMobileProps
) {
  const {
    quote,
    orderParams,
    locations,
    multiPasses,
    selectedSpace,
    capacities,
    capacityOptionLabel,
    onChangeCapacity,
    onChangeLocation,
    onChangeMultiPass,
    onContinue,
  } = props;

  return (
    <CheckoutBoxMobile
      quote={quote}
      orderParams={orderParams}
      selectedSpace={selectedSpace}
      onContinue={onContinue}
      pickers={[
        <PartnerOfferingLocationPicker
          value={orderParams.partnerOfferingLocationID}
          locations={locations}
          onChange={onChangeLocation}
          override={pickerOverride}
        />,
        <CapacityPicker
          capacities={capacities}
          value={orderParams.capacity}
          onChange={onChangeCapacity}
          override={pickerOverride}
          optionLabel={capacityOptionLabel}
        />,
        <MultiPassPicker
          multiPasses={multiPasses}
          value={orderParams.addOnID}
          onChange={onChangeMultiPass}
          override={pickerOverride}
        />,
      ]}
    />
  );
}

interface MeetingRoomCheckoutBoxMobileProps {
  quote?: Quote;
  selectedSpace?: Space | null;
  orderParams: MeetingRoomOrderParams;
  onNextVisibleDates: (startDate: Date, endDate: Date) => void;
  onChangeDateRange: (dateRange?: MeetingRoomDateRange) => void;
  onContinue: () => void;
  isOutsideRange: (date: Date) => boolean;
  isBlockedDate: (date: Date) => boolean;
  onSelectDate: (date: Date) => void;
  timeslots: AvailableTimeSlot[];
  firstDayOfWeek?: FirstDayOfWeek;
  availableDaysError?: string;
  availableTimeSlotsError?: string;
  // UI overrides
  datePickerPlaceholder?: string;
}

export function MeetingRoomCheckoutBoxMobile(
  props: MeetingRoomCheckoutBoxMobileProps
) {
  const {
    quote,
    orderParams,
    selectedSpace,
    timeslots,
    firstDayOfWeek,
    onChangeDateRange,
    onContinue,
    isOutsideRange,
    isBlockedDate,
    onNextVisibleDates,
    onSelectDate,
    availableDaysError,
    availableTimeSlotsError,
    datePickerPlaceholder,
  } = props;
  const { fbt } = useI18n();

  return (
    <CheckoutBoxMobile
      quote={quote}
      orderParams={orderParams}
      selectedSpace={selectedSpace}
      onContinue={onContinue}
      pickers={[
        <MeetingRoomDateRangePicker
          timeslots={timeslots}
          firstDayOfWeek={firstDayOfWeek}
          view="modal"
          ctaTitle={fbt("Save")}
          value={
            isHourlyValue(orderParams.dateRange)
              ? orderParams.dateRange
              : undefined
          }
          onComplete={onChangeDateRange}
          onNextVisibleDates={onNextVisibleDates}
          onSelectDate={onSelectDate}
          isBlockedDate={isBlockedDate}
          isOutsideRange={isOutsideRange}
          override={pickerOverride}
          placeholder={datePickerPlaceholder}
          disabled={!!availableDaysError || !!availableTimeSlotsError}
        />,
      ]}
    />
  );
}

interface DayPassCheckoutPageMobileProps {
  quoteError: string;
  includeQuantity: boolean;
  orderParamsError: DayPassOrderParamsErrors | undefined;
  locations: PartnerOfferingLocation[];
  selectedSpace?: Space | null;
  orderParams: DayPassOrderParams;
  onNextVisibleDates: (startDate: Date, endDate: Date) => void;
  onChangeLocation: (spaceID: string) => void;
  onChangeQuantity: (qty: number) => void;
  onChangeDateRange: (dateRange?: DayPassDateRange) => void;
  isOutsideRange: (date: Date) => boolean;
  isBlockedDate: (date: Date) => boolean;
  firstDayOfWeek?: FirstDayOfWeek;
}

export function DayPassCheckoutPageMobile(
  props: DayPassCheckoutPageMobileProps
) {
  const {
    includeQuantity,
    quoteError,
    orderParamsError,
    orderParams,
    locations,
    firstDayOfWeek,
    onNextVisibleDates,
    onChangeLocation,
    onChangeDateRange,
    onChangeQuantity,
    isOutsideRange,
    isBlockedDate,
  } = props;
  const { fbt } = useI18n();
  let error;

  if (orderParamsError?.dateRange) {
    error = <Text color="error">{orderParamsError?.dateRange}</Text>;
  } else if (orderParamsError?.spaceID) {
    error = <Text color="error">{orderParamsError?.spaceID}</Text>;
  } else if (includeQuantity && quoteError) {
    error = <QuantityNotAvailableErrorMessage orderParams={orderParams} />;
  }

  if (includeQuantity) {
    return (
      <Container>
        <Spacer size={12} />
        <LocationSpacePicker
          value={orderParams.spaceID}
          locations={locations}
          onChange={onChangeLocation}
          override={pickerOverride}
        />
        <Spacer size={16} />
        <Divider />
        <Spacer size={16} />
        <Row>
          <DateRangePicker
            firstDayOfWeek={firstDayOfWeek}
            view="modal"
            ctaTitle={fbt("Save")}
            value={orderParams.dateRange}
            isBlockedDate={isBlockedDate}
            isOutsideRange={isOutsideRange}
            onNextVisibleDates={onNextVisibleDates}
            onComplete={(v) => onChangeDateRange(v)}
            override={pickerOverride}
          />
          <QuantityPicker
            value={orderParams.quantity}
            onChange={onChangeQuantity}
            override={pickerOverride}
          />
        </Row>

        {error && (
          <Fragment>
            <Spacer size={24} />
            <Text color="error">{error}</Text>
            <Spacer size={16} />
          </Fragment>
        )}
      </Container>
    );
  }

  return (
    <Fragment>
      <Row>
        <LocationSpacePicker
          value={orderParams.spaceID}
          locations={locations}
          onChange={onChangeLocation}
          override={pickerOverride}
        />
        <DateRangePicker
          firstDayOfWeek={firstDayOfWeek}
          view="modal"
          ctaTitle={fbt("Save")}
          value={orderParams.dateRange}
          isBlockedDate={isBlockedDate}
          isOutsideRange={isOutsideRange}
          onNextVisibleDates={onNextVisibleDates}
          onComplete={(v) => onChangeDateRange(v)}
          override={pickerOverride}
        />
      </Row>
      {error && (
        <Fragment>
          <Spacer size={24} />
          <Text color="error">{error}</Text>
          <Spacer size={16} />
        </Fragment>
      )}
    </Fragment>
  );
}

interface DayPassWithCapacityCheckoutPageMobileProps {
  quoteError: string;
  orderParamsError: DayPassWithCapacityOrderParamsErrors | undefined;
  locations: PartnerOfferingLocation[];
  capacities: number[];
  capacityOptionLabel?: string;
  selectedSpace?: Space | null;
  orderParams: DayPassWithCapacityOrderParams;
  onNextVisibleDates: (startDate: Date, endDate: Date) => void;
  onChangeLocation: (spaceID: string) => void;
  onChangeCapacity: (qty: number) => void;
  onChangeDateRange: (dateRange?: DayPassDateRange) => void;
  isOutsideRange: (date: Date) => boolean;
  isBlockedDate: (date: Date) => boolean;
  firstDayOfWeek?: FirstDayOfWeek;
}

export function DayPassWithCapacityCheckoutPageMobile(
  props: DayPassWithCapacityCheckoutPageMobileProps
) {
  const {
    capacities,
    capacityOptionLabel,
    orderParamsError,
    orderParams,
    locations,
    firstDayOfWeek,
    onNextVisibleDates,
    onChangeLocation,
    onChangeDateRange,
    onChangeCapacity,
    isOutsideRange,
    isBlockedDate,
    selectedSpace,
  } = props;
  const { fbt } = useI18n();
  let error;

  if (orderParamsError?.dateRange) {
    error = <Text color="error">{orderParamsError?.dateRange}</Text>;
  } else if (orderParamsError?.spaceID) {
    error = <Text color="error">{orderParamsError?.spaceID}</Text>;
  } else if (orderParamsError?.capacity) {
    error = <Text color="error">{orderParamsError?.capacity}</Text>;
  } else if (
    selectedSpace?.inventoryCapacity !== undefined &&
    selectedSpace?.inventoryCapacity < 1
  ) {
    error = (
      <Text color="error">
        {fbt("There are no offices available for the selected capacity.")}
      </Text>
    );
  }

  return (
    <Container>
      <Spacer size={12} />
      <PartnerOfferingLocationPicker
        value={orderParams.partnerOfferingLocationID}
        locations={locations}
        onChange={onChangeLocation}
        override={pickerOverride}
      />
      <Spacer size={16} />
      <Divider />
      <Spacer size={16} />
      <Row>
        <CapacityPicker
          capacities={capacities}
          value={orderParams.capacity}
          onChange={onChangeCapacity}
          override={pickerOverride}
          optionLabel={capacityOptionLabel}
        />
        <DateRangePicker
          firstDayOfWeek={firstDayOfWeek}
          view="modal"
          ctaTitle={fbt("Save")}
          value={orderParams.dateRange}
          isBlockedDate={isBlockedDate}
          isOutsideRange={isOutsideRange}
          onNextVisibleDates={onNextVisibleDates}
          onComplete={(v) => onChangeDateRange(v)}
          override={pickerOverride}
        />
      </Row>
      {error && (
        <Fragment>
          <Spacer size={24} />
          <Text color="error">{error}</Text>
          <Spacer size={16} />
        </Fragment>
      )}
    </Container>
  );
}

interface DailyAccessCheckoutPageMobileProps {
  quoteError: string;
  includeQuantity: boolean;
  limitQuantityPicker?: number;
  locations: PartnerOfferingLocation[];
  orderParams: DailyAccessOrderParams;
  multiPasses: SpaceOffering[];
  orderParamsError: DailyAccessOrderParamsErrors | undefined;
  onNextVisibleDates: (startDate: Date, endDate: Date) => void;
  onChangeLocation: (spaceID: string) => void;
  onChangeAddOnID: (addOnID: string, spaceOfferingID: string) => void;
  onChangeDateRange: (dateRange?: DayPassDateRange) => void;
  onChangeQuantity: (qty: number) => void;
  isOutsideRange: (date: Date) => boolean;
  isBlockedDate: (date: Date) => boolean;
  firstDayOfWeek?: FirstDayOfWeek;
}

export function DailyAccessCheckoutPageMobile(
  props: DailyAccessCheckoutPageMobileProps
) {
  const {
    orderParams,
    quoteError,
    includeQuantity,
    limitQuantityPicker,
    locations,
    firstDayOfWeek,
    orderParamsError,
    onNextVisibleDates,
    onChangeLocation,
    onChangeDateRange,
    onChangeQuantity,
    multiPasses,
    onChangeAddOnID,
    isOutsideRange,
    isBlockedDate,
  } = props;
  const { fbt } = useI18n();
  const entries = multiPasses.find(
    (mp) =>
      mp.addOnID === orderParams.addOnID || orderParams.addOnID === "daypass"
  );
  const displayQuantityField = useOfferingDailyAccessQuantityFieldFeatureFlag();
  let error;

  if (orderParamsError?.dateRange) {
    error = <Text color="error">{orderParamsError?.dateRange}</Text>;
  } else if (orderParamsError?.spaceID) {
    error = <Text color="error">{orderParamsError?.spaceID}</Text>;
  } else if (orderParamsError?.addOnID) {
    error = <Text color="error">{orderParamsError?.addOnID}</Text>;
  } else if (includeQuantity && quoteError) {
    error = <QuantityNotAvailableErrorMessage orderParams={orderParams} />;
  }

  return (
    <Container>
      {error && (
        <Fragment>
          <Container padding={16}>{error}</Container>
          <Divider />
        </Fragment>
      )}
      <Spacer size={8} />
      <LocationSpacePicker
        value={orderParams.spaceID}
        locations={locations}
        onChange={onChangeLocation}
        override={pickerOverride}
      />
      <Spacer size={16} />
      <Divider />
      <Spacer size={16} />
      <Row>
        <MultiPassPicker
          multiPasses={multiPasses}
          value={orderParams.addOnID}
          onChange={onChangeAddOnID}
          override={pickerOverride}
          icon="box"
        />
        {entries?.numberOfEntries && entries.numberOfEntries === 1 && (
          <DateRangePicker
            firstDayOfWeek={firstDayOfWeek}
            view="modal"
            ctaTitle={fbt("Save")}
            value={orderParams.dateRange}
            isBlockedDate={isBlockedDate}
            isOutsideRange={isOutsideRange}
            onNextVisibleDates={onNextVisibleDates}
            onComplete={(v) => onChangeDateRange(v)}
            override={pickerOverride}
          />
        )}
      </Row>
      <Divider />
      <Spacer size={16} />
      <Row>
        {entries?.numberOfEntries &&
          entries.numberOfEntries === 1 &&
          includeQuantity &&
          displayQuantityField && (
            <QuantityPicker
              value={orderParams.quantity}
              onChange={onChangeQuantity}
              override={pickerOverride}
              limitQuantityPicker={limitQuantityPicker}
              label="Attendees:"
              icon="capacity"
            />
          )}
      </Row>
    </Container>
  );
}

interface DayPassWithCapacityCheckoutBoxMobileProps {
  quote?: Quote;
  quoteError: string;
  orderParamsError: DayPassWithCapacityOrderParamsErrors | undefined;
  locations: PartnerOfferingLocation[];
  capacities: number[];
  capacityOptionLabel?: string;
  selectedSpace?: Space | null;
  orderParams: DayPassWithCapacityOrderParams;
  onNextVisibleDates: (startDate: Date, endDate: Date) => void;
  onChangeLocation: (spaceID: string) => void;
  onChangeCapacity: (qty: number) => void;
  onChangeDateRange: (dateRange?: DayPassDateRange) => void;
  onContinue: () => void;
  isOutsideRange: (date: Date) => boolean;
  isBlockedDate: (date: Date) => boolean;
  firstDayOfWeek?: FirstDayOfWeek;
  // UI overrides
  datePickerPlaceholder?: string;
}

export function DayPassWithCapacityCheckoutBoxMobile(
  props: DayPassWithCapacityCheckoutBoxMobileProps
) {
  const {
    quote,
    orderParamsError,
    orderParams,
    locations,
    capacities,
    capacityOptionLabel,
    selectedSpace,
    firstDayOfWeek,
    onNextVisibleDates,
    onChangeLocation,
    onChangeDateRange,
    onChangeCapacity,
    onContinue,
    isOutsideRange,
    isBlockedDate,
    datePickerPlaceholder,
  } = props;
  const disabled = !quote || !orderParams.spaceID || !orderParams.dateRange;
  const { fbt } = useI18n();
  let error;

  if (orderParamsError?.dateRange) {
    error = <Text color="error">{orderParamsError?.dateRange}</Text>;
  } else if (orderParamsError?.spaceID) {
    error = <Text color="error">{orderParamsError?.spaceID}</Text>;
  } else if (orderParamsError?.capacity) {
    error = <Text color="error">{orderParamsError?.capacity}</Text>;
  } else if (
    selectedSpace?.inventoryCapacity !== undefined &&
    selectedSpace?.inventoryCapacity < 1
  ) {
    error = (
      <Text color="error">
        {fbt("There are no offices available for the selected capacity.")}
      </Text>
    );
  }

  return (
    <div className="checkout-box-mobile">
      {error && (
        <Fragment>
          <Container padding={16}>{error}</Container>
          <Divider />
        </Fragment>
      )}
      <Fragment>
        <div className="checkout-box-mobile-picker">
          <PartnerOfferingLocationPicker
            value={orderParams.partnerOfferingLocationID}
            locations={locations}
            onChange={onChangeLocation}
            override={pickerOverride}
          />
        </div>
        <Divider />
        <Row>
          <div className="checkout-box-mobile-picker">
            <CapacityPicker
              capacities={capacities}
              value={orderParams.capacity}
              onChange={onChangeCapacity}
              override={pickerOverride}
              optionLabel={capacityOptionLabel}
            />
          </div>
          <div className="checkout-box-mobile-picker">
            <DateRangePicker
              firstDayOfWeek={firstDayOfWeek}
              view="modal"
              ctaTitle={fbt("Save")}
              value={orderParams.dateRange}
              isBlockedDate={isBlockedDate}
              isOutsideRange={isOutsideRange}
              onNextVisibleDates={onNextVisibleDates}
              onComplete={(v) => onChangeDateRange(v)}
              override={pickerOverride}
              placeholder={datePickerPlaceholder}
            />
          </div>
        </Row>
      </Fragment>
      <Divider />
      <CheckoutCTA
        disabled={disabled}
        quote={quote}
        error={error}
        selectedSpace={selectedSpace}
        onContinue={onContinue}
      />
      <CheckoutInfoMessage quote={quote} orderParams={orderParams} />
    </div>
  );
}

interface DayPassCheckoutBoxMobileProps {
  quote?: Quote;
  quoteError: string;
  includeQuantity: boolean;
  orderParamsError: DayPassOrderParamsErrors | undefined;
  locations: PartnerOfferingLocation[];
  selectedSpace?: Space | null;
  orderParams: DayPassOrderParams;
  onNextVisibleDates: (startDate: Date, endDate: Date) => void;
  onChangeLocation: (spaceID: string) => void;
  onChangeQuantity: (qty: number) => void;
  onChangeDateRange: (dateRange?: DayPassDateRange) => void;
  onContinue: () => void;
  isOutsideRange: (date: Date) => boolean;
  isBlockedDate: (date: Date) => boolean;
  firstDayOfWeek?: FirstDayOfWeek;
  // UI overrides
  datePickerPlaceholder?: string;
}

export function DayPassCheckoutBoxMobile(props: DayPassCheckoutBoxMobileProps) {
  const {
    includeQuantity,
    quote,
    quoteError,
    orderParamsError,
    orderParams,
    locations,
    selectedSpace,
    firstDayOfWeek,
    onNextVisibleDates,
    onChangeLocation,
    onChangeDateRange,
    onChangeQuantity,
    onContinue,
    isOutsideRange,
    isBlockedDate,
    datePickerPlaceholder,
  } = props;
  const disabled = !quote || !orderParams.spaceID || !orderParams.dateRange;
  const { fbt } = useI18n();
  let error;

  if (orderParamsError?.dateRange) {
    error = <Text color="error">{orderParamsError?.dateRange}</Text>;
  } else if (orderParamsError?.spaceID) {
    error = <Text color="error">{orderParamsError?.spaceID}</Text>;
  } else if (includeQuantity && quoteError) {
    error = <QuantityNotAvailableErrorMessage orderParams={orderParams} />;
  }

  return (
    <div className="checkout-box-mobile">
      {error && (
        <Fragment>
          <Container padding={16}>{error}</Container>
          <Divider />
        </Fragment>
      )}
      {includeQuantity ? (
        <Fragment>
          <div className="checkout-box-mobile-picker">
            <LocationSpacePicker
              value={orderParams.spaceID}
              locations={locations}
              onChange={onChangeLocation}
              override={pickerOverride}
            />
          </div>
          <Divider />
          <Row>
            <div className="checkout-box-mobile-picker">
              <DateRangePicker
                firstDayOfWeek={firstDayOfWeek}
                view="modal"
                ctaTitle={fbt("Save")}
                value={orderParams.dateRange}
                isBlockedDate={isBlockedDate}
                isOutsideRange={isOutsideRange}
                onNextVisibleDates={onNextVisibleDates}
                onComplete={(v) => onChangeDateRange(v)}
                override={pickerOverride}
                placeholder={datePickerPlaceholder}
              />
            </div>
            <div className="checkout-box-mobile-picker">
              <QuantityPicker
                value={orderParams.quantity}
                onChange={onChangeQuantity}
                override={pickerOverride}
              />
            </div>
          </Row>
        </Fragment>
      ) : (
        <Fragment>
          <Row>
            <div className="checkout-box-mobile-picker">
              <LocationSpacePicker
                value={orderParams.spaceID}
                locations={locations}
                onChange={onChangeLocation}
                override={pickerOverride}
              />
            </div>
            <div className="checkout-box-mobile-picker">
              <DateRangePicker
                firstDayOfWeek={firstDayOfWeek}
                view="modal"
                ctaTitle={fbt("Save")}
                value={orderParams.dateRange}
                isBlockedDate={isBlockedDate}
                isOutsideRange={isOutsideRange}
                onNextVisibleDates={onNextVisibleDates}
                onComplete={(v) => onChangeDateRange(v)}
                override={pickerOverride}
                placeholder={datePickerPlaceholder}
              />
            </div>
          </Row>
        </Fragment>
      )}
      <Divider />
      <CheckoutCTA
        disabled={disabled}
        quote={quote}
        error={error}
        selectedSpace={selectedSpace}
        onContinue={onContinue}
      />
      <CheckoutInfoMessage quote={quote} orderParams={orderParams} />
    </div>
  );
}

interface DayOfficeCheckoutBoxMobileProps {
  quote?: Quote;
  quoteError: string;
  orderParamsError: DayOfficeOrderParamsErrors | undefined;
  selectedSpace?: Space | null;
  orderParams: DayOfficeOrderParams;
  onNextVisibleDates: (startDate: Date, endDate: Date) => void;
  onChangeDateRange: (dateRange?: DayPassDateRange) => void;
  onContinue: () => void;
  isOutsideRange: (date: Date) => boolean;
  isBlockedDate: (date: Date) => boolean;
  firstDayOfWeek?: FirstDayOfWeek;
  // UI overrides
  datePickerPlaceholder?: string;
}

export function DayOfficeCheckoutBoxMobile(
  props: DayOfficeCheckoutBoxMobileProps
) {
  const {
    quote,
    orderParams,
    selectedSpace,
    firstDayOfWeek,
    onNextVisibleDates,
    onChangeDateRange,
    onContinue,
    isOutsideRange,
    isBlockedDate,
    datePickerPlaceholder,
  } = props;
  const { fbt } = useI18n();

  return (
    <CheckoutBoxMobile
      quote={quote}
      orderParams={orderParams}
      selectedSpace={selectedSpace}
      onContinue={onContinue}
      pickers={[
        <DateRangePicker
          firstDayOfWeek={firstDayOfWeek}
          view="modal"
          ctaTitle={fbt("Save")}
          value={orderParams.dateRange}
          isBlockedDate={isBlockedDate}
          isOutsideRange={isOutsideRange}
          onNextVisibleDates={onNextVisibleDates}
          onComplete={(v) => onChangeDateRange(v)}
          override={pickerOverride}
          placeholder={datePickerPlaceholder}
        />,
      ]}
    />
  );
}

interface DailyAccessCheckoutBoxMobileProps {
  quote?: Quote;
  quoteError: string;
  includeQuantity: boolean;
  limitQuantityPicker?: number;
  orderParamsError: DailyAccessOrderParamsErrors | undefined;
  locations: PartnerOfferingLocation[];
  selectedSpace?: Space | null;
  orderParams: DailyAccessOrderParams;
  multiPasses: SpaceOffering[];
  onNextVisibleDates: (startDate: Date, endDate: Date) => void;
  onChangeLocation: (spaceID: string) => void;
  onChangeAddOnID: (addOnID: string, spaceOfferingID: string) => void;
  onChangeDateRange: (dateRange?: DayPassDateRange) => void;
  onChangeQuantity: (qty: number) => void;
  onContinue: () => void;
  isOutsideRange: (date: Date) => boolean;
  isBlockedDate: (date: Date) => boolean;
  firstDayOfWeek?: FirstDayOfWeek;
  // UI overrides
  datePickerPlaceholder?: string;
}

export function DailyAccessCheckoutBoxMobile(
  props: DailyAccessCheckoutBoxMobileProps
) {
  const {
    includeQuantity,
    limitQuantityPicker,
    quoteError,
    quote,
    orderParamsError,
    orderParams,
    locations,
    selectedSpace,
    firstDayOfWeek,
    onNextVisibleDates,
    onChangeLocation,
    onChangeDateRange,
    onChangeQuantity,
    multiPasses,
    onChangeAddOnID,
    onContinue,
    isOutsideRange,
    isBlockedDate,
    datePickerPlaceholder,
  } = props;
  const { fbt } = useI18n();
  let error;

  if (orderParamsError?.dateRange) {
    error = <Text color="error">{orderParamsError?.dateRange}</Text>;
  } else if (orderParamsError?.spaceID) {
    error = <Text color="error">{orderParamsError?.spaceID}</Text>;
  } else if (orderParamsError?.addOnID) {
    error = <Text color="error">{orderParamsError?.addOnID}</Text>;
  } else if (includeQuantity && quoteError) {
    error = <QuantityNotAvailableErrorMessage orderParams={orderParams} />;
  }

  const entries = multiPasses.find(
    (mp) =>
      mp.addOnID === orderParams.addOnID || orderParams.addOnID === "daypass"
  );
  const displayQuantityField = useOfferingDailyAccessQuantityFieldFeatureFlag();

  const disabled = !!(
    !quote ||
    !orderParams.spaceID ||
    (entries?.numberOfEntries &&
      entries.numberOfEntries === 1 &&
      !orderParams.dateRange) ||
    (entries?.numberOfEntries &&
      entries.numberOfEntries > 1 &&
      !orderParams.addOnID)
  );

  return (
    <div className="checkout-box-mobile">
      {error && (
        <Fragment>
          <Container padding={16}>{error}</Container>
          <Divider />
        </Fragment>
      )}
      <Fragment>
        <div className="checkout-box-mobile-picker">
          <LocationSpacePicker
            value={orderParams.spaceID}
            locations={locations}
            onChange={onChangeLocation}
            override={pickerOverride}
          />
        </div>
        <Divider />
        <Row>
          <div className="checkout-box-mobile-picker">
            <MultiPassPicker
              multiPasses={multiPasses}
              value={orderParams.addOnID}
              onChange={onChangeAddOnID}
              override={pickerOverride}
              icon="box"
            />
          </div>
          {entries?.numberOfEntries && entries.numberOfEntries === 1 && (
            <div className="checkout-box-mobile-picker">
              <DateRangePicker
                firstDayOfWeek={firstDayOfWeek}
                view="modal"
                ctaTitle={fbt("Save")}
                value={orderParams.dateRange}
                isBlockedDate={isBlockedDate}
                isOutsideRange={isOutsideRange}
                onNextVisibleDates={onNextVisibleDates}
                onComplete={(v) => onChangeDateRange(v)}
                override={pickerOverride}
                placeholder={datePickerPlaceholder}
              />
            </div>
          )}
        </Row>
        <Divider />
        <Row>
          {entries?.numberOfEntries &&
            entries.numberOfEntries === 1 &&
            includeQuantity &&
            displayQuantityField && (
              <div className="checkout-box-mobile-picker">
                <QuantityPicker
                  value={orderParams.quantity}
                  onChange={onChangeQuantity}
                  override={pickerOverride}
                  limitQuantityPicker={limitQuantityPicker}
                  label="Attendees:"
                  icon="capacity"
                />
              </div>
            )}
        </Row>
      </Fragment>
      <Divider />
      <CheckoutCTA
        disabled={disabled}
        quote={quote}
        error={error}
        selectedSpace={selectedSpace}
        onContinue={onContinue}
      />
      <CheckoutInfoMessage quote={quote} orderParams={orderParams} />
    </div>
  );
}
interface CheckoutBoxMobileProps {
  pickers: React.ReactNode[];
  quote?: Quote;
  error?: string;
  selectedSpace?: Space | null;
  orderParams: OrderParams;
  onContinue: () => void;
}

export function CheckoutBoxMobile(props: CheckoutBoxMobileProps) {
  const { quote, orderParams, selectedSpace, onContinue, pickers } = props;
  const disabled =
    !quote ||
    !orderParams.spaceID ||
    (orderParams.type === TemplateName.MultiPass && !orderParams.addOnID) ||
    (orderParams.type === TemplateName.DayPass && !orderParams.dateRange);

  return (
    <div className="checkout-box-mobile">
      {pickers.length >= 3 ? (
        <Container>
          <CheckoutBoxMobilePicker pickers={pickers} />
        </Container>
      ) : (
        <Row>
          <CheckoutBoxMobilePicker pickers={pickers} />
        </Row>
      )}
      <Divider />
      <CheckoutCTA
        disabled={disabled}
        quote={quote}
        selectedSpace={selectedSpace}
        onContinue={onContinue}
      />
      <CheckoutInfoMessage quote={quote} orderParams={orderParams} />
    </div>
  );
}

interface CheckoutInfoMessageProps {
  orderParams: OrderParams;
  quote?: Quote;
}

export function CheckoutInfoMessage(props: CheckoutInfoMessageProps) {
  const { orderParams, quote } = props;
  const { data: bookingPolicy } = useSpaceBookingPolicyQuery(
    orderParams.spaceID,
    getStartDateFromOrderParams(orderParams),
    getEndDateFromOrderParams(orderParams)
  );

  return (
    <Fragment>
      {quote && !orderParams.email && (
        <Container paddingHorizontal={16}>
          <Row>
            <Text size="sm" italic>
              {bookingPolicy?.checkoutWarning}
            </Text>
          </Row>
          <Spacer size={16} />
        </Container>
      )}
    </Fragment>
  );
}

interface CheckoutBoxMobilePickerProps {
  pickers: React.ReactNode[];
}

function CheckoutBoxMobilePicker(props: CheckoutBoxMobilePickerProps) {
  const { pickers } = props;
  return (
    <>
      {pickers.map((p, i) => {
        return (
          <div key={i} className="checkout-box-mobile-picker">
            {p}
          </div>
        );
      })}
    </>
  );
}

interface CheckoutCTAProps {
  quote?: Quote;
  error?: React.ReactNode;
  disabled: boolean;
  onContinue: () => void;
  selectedSpace: Space | null | undefined;
}

function CheckoutCTA(props: CheckoutCTAProps) {
  const { quote, error, selectedSpace, disabled, onContinue } = props;
  const { fbt } = useI18n();
  const isWeWork = useIsWework();
  return (
    <Container paddingVertical={8} paddingHorizontal={16}>
      <Row>
        <Container flex={1}>
          {error && (
            <Container height="100%" center>
              <Text weight="bold" align="center">
                {fbt("Not available")}
              </Text>
            </Container>
          )}
          {!quote &&
            !error &&
            selectedSpace &&
            selectedSpace.pricings[0].type === PricingType.Daily && (
              <Container height="100%" center>
                <Text weight="bold" align="center">
                  {formatCurrency(
                    selectedSpace.pricings[0].price,
                    selectedSpace?.currencyCode,
                    getSystemLocale()
                  )}
                  <Text>
                    {isWeWork ? fbt("before VAT per day") : fbt("/day")}
                  </Text>
                </Text>
              </Container>
            )}
          {quote && selectedSpace?.currencyCode && (
            <Container height="100%">
              <Row alignItems="center">
                <Text color="muted" size="sm">
                  {fbt("Total")} ({selectedSpace?.currencyCode})
                </Text>
                <Spacer size={8} direction="horizontal" />
                <Text weight="bold">
                  {formatCurrency(
                    quote.subTotal,
                    selectedSpace?.currencyCode,
                    getSystemLocale()
                  )}
                  {isWeWork && <Text> {` ${fbt("before VAT")}`}</Text>}
                </Text>
              </Row>
            </Container>
          )}
        </Container>
        <Spacer size={8} direction="horizontal" />
        <Container flex={1}>
          <Button
            onPress={onContinue}
            disabled={disabled}
            title={fbt("Continue")}
          />
        </Container>
      </Row>
    </Container>
  );
}

interface QuantityNotAvailableErrorMessageProps {
  orderParams: DayPassOrderParams | DailyAccessOrderParams;
}

export function QuantityNotAvailableErrorMessage(
  props: QuantityNotAvailableErrorMessageProps
) {
  const { orderParams } = props;
  const { fbt } = useI18n();
  const { onOpenContactForm } = useContactForm();
  return (
    <Text color="error">
      {fbt(
        "We are sorry, but there is no availability for " +
          fbt.plural("pass", orderParams.quantity || 1, {
            name: "number of passes",
            showCount: "yes",
            many: "passes",
          })
      )}
      {". "}
      <Text
        decoration="underline"
        weight="bold"
        color="error"
        onPress={onOpenContactForm}
      >
        {fbt("Talk with us")}
      </Text>{" "}
      {fbt("and we may be able to help you find a solution.")}
    </Text>
  );
}
